import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (path) => (path ? `${BACKEND_URL}/api/files/${path}` : null);
const FONT = "Cormorant Garamond";

const fmtPrice = (value) => {
  const price = String(value ?? "").trim();
  if (!price) return "—";
  if (/^\d+(\.\d+)?$/.test(price)) return `$${Number(price).toLocaleString("es-CO")}`;
  if (/^[\d.,]+$/.test(price)) return `$${price}`;
  return price;
};

const PriceLines = ({ value }) => {
  const lines = fmtPrice(value).split(/\s+\/\s+/).map((line) => line.trim()).filter(Boolean);
  return lines.map((line, index) => <span key={`${line}-${index}`} style={{ display: "block" }}>{line}</span>);
};

function preloadFont(family) {
  const id = `font-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/\s+/g, "+")}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

const COLORS = {
  primary: "#24160F",
  accent: "#8B5E3C",
  bg: "#FFFFFF",
  line: "#D8C9BA",
  textSoft: "#7C685A",
};

const getCategoryCardImg = (category) => {
  if (category.card_image_path) return imgUrl(category.card_image_path);
  if (category.card_image_url) return category.card_image_url;
  if (category.image_path) return imgUrl(category.image_path);
  return category.image_url || "";
};
const getCategoryHeroImg = (category) => {
  if (category.menu_image_path) return imgUrl(category.menu_image_path);
  if (category.menu_image_url) return category.menu_image_url;
  if (category.image_path) return imgUrl(category.image_path);
  return category.image_url || "";
};
const getMenuImage = (menu) => menu?.direct_menu_image ? (menu.direct_menu_image.startsWith("http") ? menu.direct_menu_image : imgUrl(menu.direct_menu_image)) : "";

const categoryTitle = (name) => {
  const words = String(name || "").trim().split(/\s+/);
  if (name.length <= 18 || words.length < 2) return name;
  const splitIndex = Math.ceil(words.length / 2);
  return <>{words.slice(0, splitIndex).join(" ")}<br />{words.slice(splitIndex).join(" ")}</>;
};

function GourmetInlineUnits({ units }) {
  if (!units) return null;
  const n = parseInt(units, 10);
  if (Number.isNaN(n) || n <= 1) return null;
  return <span style={{ color: COLORS.accent, fontSize: "0.92rem", letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{units} uds</span>;
}

function GourmetInlineBadges({ badges }) {
  if (!badges?.length) return null;
  return <div style={{ display: "inline-flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>{badges.map((badge) => <span key={badge} style={{ display: "inline-flex", alignItems: "center", padding: "5px 12px", borderRadius: "999px", background: "#E5F6EA", color: "#1E6B37", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }} data-testid={`public-badge-${badge.toLowerCase().replace(/\s+/g, "-")}`}>{badge}</span>)}</div>;
}

function GourmetAllergensLine({ allergens }) {
  if (!allergens?.length) return null;
  return <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>{allergens.map((allergen) => <span key={allergen} style={{ display: "inline-flex", alignItems: "center", padding: "5px 12px", borderRadius: "999px", background: "#F7E6EA", color: "#7A2038", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }} data-testid={`public-allergen-${allergen.toLowerCase().replace(/\s+/g, "-")}`}>{allergen}</span>)}</div>;
}

function ProductRow({ product, isLast }) {
  return (
    <article style={{ padding: isLast ? "0 0 6px" : "0 0 26px", marginBottom: isLast ? 0 : "26px" }} data-testid={`public-product-${product.assignment_id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "20px", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
            <h3 style={{ fontFamily: `'${FONT}', serif`, fontSize: "2rem", lineHeight: 1, margin: 0, color: COLORS.primary, fontWeight: 600 }} data-testid={`public-product-title-${product.assignment_id}`}>{product.name}</h3>
            <GourmetInlineUnits units={product.product_units} />
            <GourmetInlineBadges badges={product.badges || []} />
          </div>
          <GourmetAllergensLine allergens={product.allergens || []} />
          {product.description && <p style={{ margin: "10px 0 0", fontSize: "1rem", lineHeight: 1.45, color: COLORS.textSoft, maxWidth: "760px" }} data-testid={`public-product-description-${product.assignment_id}`}>{product.description}</p>}
        </div>
        <div style={{ flexShrink: 0, color: COLORS.primary, fontFamily: `'${FONT}', serif`, fontWeight: 700, fontSize: "1.75rem", whiteSpace: "nowrap", paddingTop: "2px", textAlign: "right" }} data-testid={`public-product-price-${product.assignment_id}`}><PriceLines value={product.price} /></div>
      </div>
    </article>
  );
}

function LoadingState() {
  return <div style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="public-menu-loading"><div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", color: COLORS.accent }}><div className="animate-spin" style={{ width: "34px", height: "34px", border: `2px solid ${COLORS.line}`, borderTopColor: COLORS.accent, borderRadius: "50%" }} data-testid="public-menu-loading-spinner" /><span style={{ fontFamily: `'${FONT}', serif`, fontSize: "1.25rem" }} data-testid="public-menu-loading-label">Cargando menú</span></div></div>;
}

function CategoryLoadingState() {
  return <div style={{ position: "fixed", inset: 0, zIndex: 100, background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="category-image-loading"><div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", color: COLORS.accent }}><div className="animate-spin" style={{ width: "34px", height: "34px", border: `2px solid ${COLORS.line}`, borderTopColor: COLORS.accent, borderRadius: "50%" }} data-testid="category-image-loading-spinner" /><span style={{ fontFamily: `'${FONT}', serif`, fontSize: "1.25rem" }} data-testid="category-image-loading-label">Cargando menú</span></div></div>;
}

function DirectMenu({ menu, categories, heroImage, onHeroLoad }) {
  const visibleCategories = useMemo(() => categories.filter((category) => category.subcategories?.some((subcategory) => subcategory.products?.length)), [categories]);
  const [activeCategoryId, setActiveCategoryId] = useState(visibleCategories[0]?.id || "");
  const categoryRefs = useRef({});
  const onlyGeneralCategory = useMemo(() => visibleCategories.length === 1 && visibleCategories[0].name.trim().toLowerCase() === "general", [visibleCategories]);

  useEffect(() => {
    setActiveCategoryId(visibleCategories[0]?.id || "");
  }, [visibleCategories]);

  useEffect(() => {
    if (!visibleCategories.length) return undefined;
    let frameId = null;
    const updateActiveTab = () => {
      const passed = visibleCategories.filter((category) => categoryRefs.current[category.id]?.getBoundingClientRect().top <= 120);
      setActiveCategoryId((passed.at(-1) || visibleCategories[0]).id);
    };
    const onScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateActiveTab();
      });
    };
    updateActiveTab();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [visibleCategories]);

  const scrollToCategory = (categoryId) => {
    setActiveCategoryId(categoryId);
    categoryRefs.current[categoryId]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="direct-menu-view">
      {heroImage && <section style={{ width: "100%", aspectRatio: "2.5 / 1", maxHeight: "620px", overflow: "hidden", background: COLORS.accent }} data-testid="direct-menu-hero"><img src={heroImage} alt="" loading="eager" decoding="async" onLoad={onHeroLoad} onError={onHeroLoad} style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }} data-testid="direct-menu-hero-image" /></section>}
    <main style={{ width: "100%", maxWidth: "1120px", boxSizing: "border-box", overflowX: "clip", margin: "0 auto", padding: "56px clamp(20px, 8vw, 32px) 90px" }}>
      <header style={{ marginBottom: "56px", textAlign: "center" }} data-testid="direct-menu-header">
        <h1 style={{ fontFamily: `'${FONT}', serif`, fontSize: "clamp(3rem, 6vw, 5rem)", lineHeight: 0.95, margin: 0, color: COLORS.primary, fontWeight: 800, overflowWrap: "anywhere" }} data-testid="direct-menu-title">{menu.name}</h1>
        <div style={{ width: "96px", height: "2px", background: COLORS.line, margin: "22px auto 0" }} />
        {menu.description && <p style={{ margin: "16px auto 0", fontSize: "1.1rem", lineHeight: 1.5, color: COLORS.textSoft, maxWidth: "660px" }} data-testid="direct-menu-description">{menu.description}</p>}
      </header>
      {!onlyGeneralCategory && visibleCategories.length > 0 && <nav style={{ position: "sticky", top: 0, zIndex: 20, margin: "0 -1px 40px", padding: "12px 0", background: COLORS.bg, borderTop: `1px solid ${COLORS.line}`, borderBottom: `1px solid ${COLORS.line}`, overflowX: "auto" }} data-testid="subcategory-tabs">
        <div style={{ display: "flex", gap: "24px", minWidth: "max-content", padding: "0 2px" }}>
          {visibleCategories.map((category) => <button key={category.id} onClick={() => scrollToCategory(category.id)} style={{ border: "none", borderBottom: `2px solid ${activeCategoryId === category.id ? COLORS.accent : "transparent"}`, background: "transparent", color: activeCategoryId === category.id ? COLORS.primary : COLORS.textSoft, padding: "7px 0", cursor: "pointer", fontFamily: `'${FONT}', serif`, fontSize: "1.1rem", fontWeight: activeCategoryId === category.id ? 800 : 600, whiteSpace: "nowrap" }} aria-selected={activeCategoryId === category.id} data-testid={`direct-category-tab-${category.id}`}>{category.name}</button>)}
        </div>
      </nav>}
      <div style={{ display: "grid", gap: "68px" }} data-testid="direct-menu-categories">
        {visibleCategories.map((category) => (
          <section key={category.id} ref={(element) => { categoryRefs.current[category.id] = element; }} style={{ scrollMarginTop: "104px" }} data-testid={`direct-menu-category-${category.id}`}>
            {!onlyGeneralCategory && <div style={{ borderTop: `1px solid ${COLORS.line}`, paddingTop: "26px", marginBottom: "38px" }}>
              <h2 style={{ fontFamily: `'${FONT}', serif`, fontSize: "clamp(1.7rem, 3vw, 2.35rem)", lineHeight: 1, margin: 0, color: COLORS.accent, fontWeight: 700, letterSpacing: "0.02em", textAlign: "center", overflowWrap: "anywhere" }} data-testid={`direct-menu-category-title-${category.id}`}>-- {category.name} --</h2>
              {category.description && <p style={{ margin: "14px 0 0", fontSize: "1.05rem", lineHeight: 1.5, color: COLORS.textSoft, maxWidth: "760px" }} data-testid={`direct-menu-category-description-${category.id}`}>{category.description}</p>}
            </div>}
            <div style={{ display: "grid", gap: "44px" }}>
              {category.subcategories.filter((subcategory) => subcategory.products?.length).map((subcategory) => {
                const productGroups = subcategory.products.reduce((groups, product) => {
                  const type = product.subcategory_type?.trim() || "";
                  const group = groups.find((item) => item.type === type);
                  if (group) group.products.push(product);
                  else groups.push({ type, products: [product] });
                  return groups;
                }, []);
                const normalizedSubcategoryName = subcategory.name.trim().toLowerCase();
                const showSubcategoryTitle = normalizedSubcategoryName !== "general" && normalizedSubcategoryName !== category.name.trim().toLowerCase();
                return <section key={subcategory.id} data-testid={`direct-menu-subcategory-${subcategory.id}`}>
                  {showSubcategoryTitle && <div style={{ marginBottom: "28px", textAlign: "center" }}>
                    <h3 style={{ fontFamily: `'${FONT}', serif`, fontSize: "1.7rem", lineHeight: 1, margin: 0, color: COLORS.accent, fontWeight: 700, letterSpacing: "0.02em" }} data-testid={`direct-menu-subcategory-title-${subcategory.id}`}>-- {subcategory.name} --</h3>
                  </div>}
                  {productGroups.map((group, groupIndex) => <section key={group.type || `general-${groupIndex}`} style={{ marginBottom: groupIndex === productGroups.length - 1 ? 0 : "38px" }} data-testid={`direct-menu-type-group-${subcategory.id}-${groupIndex}`}>
                    {group.type && <h4 style={{ fontFamily: `'${FONT}', serif`, fontSize: "1.5rem", lineHeight: 1, margin: "0 0 20px", color: COLORS.accent, fontWeight: 800, letterSpacing: "0.02em" }} data-testid={`direct-menu-type-title-${subcategory.id}-${groupIndex}`}>{group.type}</h4>}
                    <div>{group.products.map((product, index) => <ProductRow key={product.assignment_id} product={product} isLast={index === group.products.length - 1} />)}</div>
                  </section>)}
                </section>;
              })}
            </div>
          </section>
        ))}
      </div>
      {visibleCategories.length === 0 && <p style={{ color: COLORS.textSoft, textAlign: "center", fontSize: "1.1rem" }} data-testid="direct-menu-empty">Este menú todavía no tiene productos.</p>}
    </main>
    </div>
  );
}

export default function PublicMenu() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadedCategoryImage, setLoadedCategoryImage] = useState("");
  const [loadedCoverImages, setLoadedCoverImages] = useState([]);
  const [activeSubcategoryId, setActiveSubcategoryId] = useState("");
  const subcategoryRefs = useRef({});
  const pendingSubcategoryId = useRef("");
  const pendingSubcategoryTimer = useRef(null);
  const selectedCategoryId = searchParams.get("category");
  const categories = data?.categories || [];
  const activeCategory = categories.find((category) => String(category.id) === String(selectedCategoryId));
  const activeCategoryImage = activeCategory ? getCategoryHeroImg(activeCategory) : "";
  const directMenu = Boolean(data?.menu?.direct_menu);
  const directHeroImage = getMenuImage(data?.menu);
  const coverImageSources = [...new Set(categories.map(getCategoryCardImg).filter(Boolean))];
  const visibleSubcategories = (activeCategory?.subcategories || []).filter((subcategory) => (subcategory.products || []).length > 0);
  const visibleSubcategoryKey = visibleSubcategories.map((subcategory) => subcategory.id).join("|");

  useEffect(() => { preloadFont(FONT); }, []);
  useEffect(() => {
    setLoading(true);
    api.public.getMenu(slug).then((response) => {
      setData(response.data);
      document.title = response.data.menu?.name || "Menú";
    }).catch((err) => setError(err.response?.data?.detail || "Menú no encontrado")).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    pendingSubcategoryId.current = "";
    window.clearTimeout(pendingSubcategoryTimer.current);
    setActiveSubcategoryId(visibleSubcategories[0]?.id || "");
  }, [activeCategory?.id]);

  useEffect(() => {
    if (!activeCategory || !visibleSubcategories.length) return undefined;
    let frameId = null;
    const updateActiveTab = () => {
      if (pendingSubcategoryId.current) return;
      const passedSections = visibleSubcategories.filter((subcategory) => {
        const target = subcategoryRefs.current[subcategory.id];
        return target && target.getBoundingClientRect().top <= 120;
      });
      setActiveSubcategoryId((passedSections.at(-1) || visibleSubcategories[0]).id);
    };
    const onScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateActiveTab();
      });
    };
    updateActiveTab();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [activeCategory?.id, visibleSubcategoryKey]);

  const scrollToSubcategory = (subcategoryId) => {
    pendingSubcategoryId.current = subcategoryId;
    setActiveSubcategoryId(subcategoryId);
    subcategoryRefs.current[subcategoryId]?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.clearTimeout(pendingSubcategoryTimer.current);
    pendingSubcategoryTimer.current = window.setTimeout(() => {
      pendingSubcategoryId.current = "";
      setActiveSubcategoryId(subcategoryId);
    }, 1200);
  };

  const selectCategory = (categoryId) => {
    setSearchParams(categoryId ? { category: categoryId } : {});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <LoadingState />;
  if (error || !data) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FAF8F5" }} data-testid="public-menu-error"><div style={{ textAlign: "center" }}><div style={{ fontFamily: `'${FONT}', serif`, fontSize: "4rem", color: COLORS.primary }}>404</div><p style={{ marginTop: "12px", color: COLORS.textSoft }}>{error || "Menú no encontrado"}</p></div></div>;
  const categoryDetailPending = Boolean(activeCategory && activeCategoryImage && loadedCategoryImage !== activeCategoryImage);
  const directHeroPending = Boolean(directMenu && directHeroImage && loadedCategoryImage !== directHeroImage);
  const coverPending = Boolean(!directMenu && !activeCategory && coverImageSources.some((source) => !loadedCoverImages.includes(source)));

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, fontFamily: `'${FONT}', serif`, color: COLORS.primary, width: "100%", overflowX: "clip" }} data-testid="public-menu-page">
      {directMenu ? <div style={{ visibility: directHeroPending ? "hidden" : "visible" }}><DirectMenu menu={data.menu} categories={categories} heroImage={directHeroImage} onHeroLoad={() => setLoadedCategoryImage(directHeroImage)} /></div> : !activeCategory ? (
        <main style={{ maxWidth: "1120px", margin: "0 auto", padding: "56px 32px 90px", visibility: coverPending ? "hidden" : "visible" }} data-testid="public-menu-cover">
          <header style={{ marginBottom: "42px", textAlign: "center" }} data-testid="public-menu-cover-title">
            <h1 style={{ fontFamily: `'${FONT}', serif`, fontSize: "clamp(3rem, 6vw, 5rem)", lineHeight: 0.95, margin: 0, color: COLORS.primary, fontWeight: 800 }}>{data.menu.name}</h1>
            <div style={{ width: "96px", height: "2px", background: COLORS.line, margin: "22px auto 0" }} />
            {data.menu.description && <p style={{ margin: "16px auto 0", fontSize: "1.1rem", lineHeight: 1.5, color: COLORS.textSoft, maxWidth: "660px" }}>{data.menu.description}</p>}
          </header>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "28px" }} data-testid="public-menu-cover-grid">
            {categories.map((category) => {
              const categoryImage = getCategoryCardImg(category);
              return (
                <button key={category.id} onClick={() => selectCategory(category.id)} style={{ position: "relative", width: "60%", justifySelf: "center", height: "clamp(150px, 18.7vw, 238px)", padding: 0, border: "none", borderRadius: "28px", overflow: "hidden", cursor: "pointer", background: "#8B5E3C", textAlign: "left" }} data-testid={`category-card-${category.id}`}>
                  {categoryImage && <img src={categoryImage} alt={category.name} loading="eager" decoding="async" onLoad={() => setLoadedCoverImages((previous) => previous.includes(categoryImage) ? previous : [...previous, categoryImage])} onError={() => setLoadedCoverImages((previous) => previous.includes(categoryImage) ? previous : [...previous, categoryImage])} style={{ position: "absolute", zIndex: 0, inset: 0, width: "100%", height: "100%", objectFit: "cover" }} data-testid={`category-img-${category.id}`} />}
                  {categoryImage && <span style={{ position: "absolute", zIndex: 1, inset: 0, background: "linear-gradient(110deg, rgba(12, 8, 5, 0.04) 35%, rgba(12, 8, 5, 0.78) 100%)" }} />}
                  <span style={{ position: "absolute", zIndex: 2, right: "8%", bottom: "18%", color: "#FFFFFF", fontFamily: `'${FONT}', serif`, fontSize: "1.7rem", lineHeight: 0.95, fontWeight: 800, textAlign: "right", textShadow: categoryImage ? "0 2px 10px rgba(0,0,0,0.72)" : "none", maxWidth: "48%" }} data-testid={`public-category-title-${category.id}`}>{categoryTitle(category.name)}</span>
                </button>
              );
            })}
          </div>
          {categories.length === 0 && <p style={{ color: COLORS.textSoft, textAlign: "center", fontSize: "1.1rem" }} data-testid="public-menu-empty">Este menú todavía no tiene categorías.</p>}
        </main>
      ) : (
        <main style={{ visibility: categoryDetailPending ? "hidden" : "visible" }} data-testid={`category-detail-view-${activeCategory.id}`}>
          <section style={{ width: "100%", margin: "0 0 56px", background: activeCategoryImage ? "transparent" : "#8B5E3C", aspectRatio: "2.5 / 1", maxHeight: "620px", overflow: "hidden" }} data-testid={`category-hero-${activeCategory.id}`}>
            {activeCategoryImage && <img src={activeCategoryImage} alt={activeCategory.name} loading="eager" decoding="sync" onLoad={() => setLoadedCategoryImage(activeCategoryImage)} onError={() => setLoadedCategoryImage(activeCategoryImage)} style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }} data-testid={`category-hero-image-${activeCategory.id}`} />}
          </section>
          <div style={{ maxWidth: "1120px", margin: "0 auto", padding: "0 32px 90px" }}>
            <section style={{ background: "#FFFFFF" }} data-testid={`menu-section-${activeCategory.id}`}>
              <div style={{ marginBottom: "26px", display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "24px", flexWrap: "wrap" }}>
                <div>
                  <h1 style={{ fontFamily: `'${FONT}', serif`, fontSize: "clamp(2.35rem, 4vw, 3.6rem)", lineHeight: 0.95, margin: 0, color: COLORS.primary, fontWeight: 800 }} data-testid={`public-category-detail-title-${activeCategory.id}`}>{activeCategory.name}</h1>
                  <div style={{ width: "96px", height: "2px", background: COLORS.line, marginTop: "14px" }} />
                  {activeCategory.description && <p style={{ margin: "14px 0 0", fontSize: "1.05rem", lineHeight: 1.5, color: COLORS.textSoft, maxWidth: "760px" }}>{activeCategory.description}</p>}
                </div>
                <button onClick={() => selectCategory(null)} style={{ border: "none", background: "transparent", color: COLORS.accent, padding: "8px 0", cursor: "pointer", fontFamily: `'${FONT}', serif`, fontSize: "1.2rem", fontWeight: 700, whiteSpace: "nowrap" }} data-testid="back-to-categories-btn">← Todas las categorías</button>
              </div>
              {visibleSubcategories.length > 0 && (
                <nav style={{ position: "sticky", top: 0, zIndex: 20, margin: "0 -1px 40px", padding: "12px 0", background: "#FFFFFF", borderTop: `1px solid ${COLORS.line}`, borderBottom: `1px solid ${COLORS.line}`, overflowX: "auto" }} data-testid="subcategory-tabs">
                  <div style={{ display: "flex", gap: "24px", minWidth: "max-content", padding: "0 2px" }}>
                    {visibleSubcategories.map((subcategory) => <button key={subcategory.id} onClick={() => scrollToSubcategory(subcategory.id)} style={{ border: "none", borderBottom: `2px solid ${activeSubcategoryId === subcategory.id ? COLORS.accent : "transparent"}`, background: "transparent", color: activeSubcategoryId === subcategory.id ? COLORS.primary : COLORS.textSoft, padding: "7px 0", cursor: "pointer", fontFamily: `'${FONT}', serif`, fontSize: "1.1rem", fontWeight: activeSubcategoryId === subcategory.id ? 800 : 600, whiteSpace: "nowrap" }} aria-selected={activeSubcategoryId === subcategory.id} data-testid={`subcategory-tab-${subcategory.id}`}>{subcategory.name}</button>)}
                  </div>
                </nav>
              )}
              <div style={{ display: "grid", gap: "44px" }}>
                {visibleSubcategories.map((subcategory) => {
                  const productGroups = subcategory.products.reduce((groups, product) => {
                    const type = product.subcategory_type?.trim() || "";
                    const group = groups.find((item) => item.type === type);
                    if (group) group.products.push(product);
                    else groups.push({ type, products: [product] });
                    return groups;
                  }, []);
                  return <div key={subcategory.id} ref={(element) => { subcategoryRefs.current[subcategory.id] = element; }} data-subcategory-id={subcategory.id} style={{ scrollMarginTop: "104px" }} data-testid={`menu-subsection-${subcategory.id}`}>
                    <div style={{ marginBottom: "28px", textAlign: "center" }}>
                      <h2 style={{ fontFamily: `'${FONT}', serif`, fontSize: "1.7rem", lineHeight: 1, margin: 0, color: COLORS.accent, fontWeight: 700, letterSpacing: "0.02em" }} data-testid={`public-subcategory-title-${subcategory.id}`}>-- {subcategory.name} --</h2>
                      {subcategory.description && <p style={{ margin: "10px auto 0", fontSize: "0.98rem", lineHeight: 1.45, color: COLORS.textSoft, maxWidth: "720px" }}>{subcategory.description}</p>}
                    </div>
                    <div>
                      {productGroups.map((group, groupIndex) => <section key={group.type || `general-${groupIndex}`} style={{ marginBottom: groupIndex === productGroups.length - 1 ? 0 : "38px" }} data-testid={`subcategory-type-group-${subcategory.id}-${groupIndex}`}>
                        {group.type && <h3 style={{ fontFamily: `'${FONT}', serif`, fontSize: "1.5rem", lineHeight: 1, margin: "0 0 20px", color: COLORS.accent, fontWeight: 800, letterSpacing: "0.02em", textAlign: "left" }} data-testid={`subcategory-type-title-${subcategory.id}-${groupIndex}`}>{group.type}</h3>}
                        <div>{group.products.map((product, index) => <ProductRow key={product.assignment_id} product={product} isLast={index === group.products.length - 1} />)}</div>
                      </section>)}
                    </div>
                  </div>;
                })}
              </div>
              {!activeCategory.subcategories?.some((sub) => sub.products?.length) && <p style={{ color: COLORS.textSoft, fontSize: "1.05rem" }} data-testid="category-products-empty">Esta categoría todavía no tiene productos.</p>}
            </section>
          </div>
        </main>
      )}
      {coverPending && <CategoryLoadingState />}
      {categoryDetailPending && <CategoryLoadingState />}
      {directHeroPending && <CategoryLoadingState />}
      <style>{`
        [data-testid^="category-card-"] { transition: transform 220ms ease, box-shadow 220ms ease; }
        [data-testid^="category-card-"]:hover { transform: translateY(-5px); box-shadow: 0 18px 32px rgba(36, 22, 15, 0.18); }
        @media (max-width: 768px) {
          [data-testid="public-menu-cover"] { padding: 40px 20px 70px !important; }
          [data-testid^="category-card-"] { width: 100% !important; height: 150px !important; }
          [data-testid^="public-category-title-"] { font-size: 1.6rem !important; }
          [data-testid^="category-hero-"] { aspect-ratio: 1.55 / 1 !important; }
          [data-testid="category-detail-view"] > div, [data-testid^="category-detail-view-"] > div, [data-testid^="category-detail-view-"] > main { padding-left: 20px !important; padding-right: 20px !important; }
          [data-testid^="public-product-"] > div { gap: 14px !important; }
          [data-testid^="public-product-title-"] { font-size: 1.65rem !important; }
          [data-testid^="public-product-price-"] { font-size: 1.4rem !important; }
        }
        @media (max-width: 560px) {
          [data-testid^="public-product-"] > div { flex-direction: column !important; }
          [data-testid^="public-product-price-"] { align-self: flex-end; }
        }
      `}</style>
    </div>
  );
}